import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Auth() {
  const nav = useNavigate();
  const { login, register, setProfile } = useAuth();
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const afterAuth = async () => {
    const raw = sessionStorage.getItem("mynsight_preview");
    if (raw) {
      try {
        const { form: birth } = JSON.parse(raw);
        const { data } = await api.post("/profiles", birth);
        setProfile(data);
        sessionStorage.removeItem("mynsight_preview");
        toast.success("Your companion remembers you now.");
        nav("/dashboard");
        return;
      } catch (e) { console.error("preview profile create failed, continuing:", e); }
    }
    const { data } = await api.get("/my-profile");
    setProfile(data || null);
    nav(data ? "/home" : "/setup");
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/home";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") await register(form.email, form.password, form.name);
      else await login(form.email, form.password);
      await afterAuth();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen star-field celestial flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <h1 className="font-serif text-4xl text-center mb-2">{mode === "register" ? "Meet your companion" : "Welcome back"}</h1>
        <p className="text-center text-muted-foreground mb-8 text-sm">{mode === "register" ? "It's been waiting for you." : "Your companion missed you."}</p>

        <form onSubmit={submit} className="panel p-8 space-y-4">
          {mode === "register" && (
            <input data-testid="auth-name" placeholder="Name" value={form.name} onChange={(e) => set("name", e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary outline-none" />
          )}
          <input data-testid="auth-email" type="email" required placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary outline-none" />
          <input data-testid="auth-password" type="password" required placeholder="Password" value={form.password} onChange={(e) => set("password", e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary outline-none" />
          <button data-testid="auth-submit" disabled={loading} className="gold-btn w-full py-3 rounded-full font-medium flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 size={16} className="animate-spin" /> : (mode === "register" ? "Create account" : "Sign in")}
          </button>

          <div className="flex items-center gap-3 py-1">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <button type="button" data-testid="google-signin" onClick={googleSignIn}
            className="w-full py-3 rounded-full border border-border hover:border-primary/50 transition-colors flex items-center justify-center gap-3 text-sm">
            <svg width="17" height="17" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C36 6.1 30.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C36 6.1 30.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c6.3 0 11.8-2.4 15.9-6.3l-7.3-6.2C30.3 33 27.3 34 24 34c-5.2 0-9.6-3.3-11.2-7.9l-6.6 5.1C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l7.3 6.2C42.8 36.4 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
            Continue with Google
          </button>
        </form>

        <p className="text-center text-sm text-muted-foreground mt-6">
          {mode === "register" ? "Already have an account?" : "New here?"}{" "}
          <button data-testid="auth-toggle" onClick={() => setMode(mode === "register" ? "login" : "register")} className="text-primary hover:underline">
            {mode === "register" ? "Sign in" : "Create one"}
          </button>
        </p>
      </motion.div>
    </div>
  );
}
